/**
 * Filtering logic: apply value filters and quick search over untyped records.
 */

import { toEpochMs } from '../format';
import { getElementValues, getValue, isMissing } from './flatten';
import type { ApplyFilters, Filter, FilterLabel, FilterState, Row, Schema } from './types';

const searchCache = new WeakMap<Row[], string[]>();

function buildSearchIndex(rows: Row[]): string[] {
  let cached = searchCache.get(rows);
  if (!cached) {
    cached = rows.map((row) => JSON.stringify(row).toLowerCase());
    searchCache.set(rows, cached);
  }
  return cached;
}

function matchesQuickSearch(searchIndex: string[], index: number, query: string): boolean {
  return searchIndex[index].includes(query);
}

function matchesFilter(row: Row, filter: Filter, schema: Schema): boolean {
  const col = schema.byId.get(filter.column);
  if (!col) return true;

  const value = getValue(row, col);

  switch (filter.kind) {
    case 'contains': {
      if (isMissing(value)) return false;
      const haystack =
        typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
          ? String(value)
          : JSON.stringify(value);
      if (filter.regex) {
        try {
          const re = new RegExp(filter.text, 'i');
          return re.test(haystack);
        } catch {
          return haystack.toLowerCase().includes(filter.text.toLowerCase());
        }
      }
      return haystack.toLowerCase().includes(filter.text.toLowerCase());
    }

    case 'in': {
      if (isMissing(value)) return false;
      const needle = String(value);
      return filter.values.some((v) => String(v) === needle);
    }

    case 'range': {
      if (isMissing(value)) return false;
      let num: number | null = null;
      if (col.type === 'date') {
        num = toEpochMs(value);
      } else if (typeof value === 'number') {
        num = value;
      }
      if (num === null || !Number.isFinite(num)) return false;
      if (filter.min !== undefined && num < filter.min) return false;
      if (filter.max !== undefined && num > filter.max) return false;
      return true;
    }

    case 'dateRange': {
      if (isMissing(value)) return false;
      const ms = toEpochMs(value);
      if (ms === null) return false;
      if (filter.from) {
        const fromMs = toEpochMs(filter.from);
        if (fromMs !== null && ms < fromMs) return false;
      }
      if (filter.to) {
        const toMs = toEpochMs(filter.to);
        if (toMs !== null) {
          const endOfDay = toMs + 86400000 - 1;
          if (ms > endOfDay) return false;
        }
      }
      return true;
    }

    case 'missing': {
      const missing = isMissing(value);
      return filter.present ? !missing : missing;
    }

    case 'arrayElement': {
      if (!Array.isArray(value)) return false;
      const elements = getElementValues(value, filter.elementPath);
      return elements.some((el) => {
        if (isMissing(el)) return false;
        const elStr =
          typeof el === 'string' || typeof el === 'number' || typeof el === 'boolean'
            ? String(el)
            : JSON.stringify(el);
        if (filter.op === 'eq') {
          return elStr === filter.value;
        }
        return elStr.toLowerCase().includes(filter.value.toLowerCase());
      });
    }

    case 'arrayLength': {
      if (!Array.isArray(value)) return false;
      const len = value.length;
      if (filter.min !== undefined && len < filter.min) return false;
      if (filter.max !== undefined && len > filter.max) return false;
      return true;
    }

    default:
      return true;
  }
}

export const applyFilters: ApplyFilters = (rows, schema, state) => {
  const { filters, quickSearch } = state;
  const searchQuery = quickSearch.trim().toLowerCase();
  const hasSearch = searchQuery.length > 0;
  const searchIndex = hasSearch ? buildSearchIndex(rows) : [];

  const result: number[] = [];
  for (let i = 0; i < rows.length; i++) {
    if (hasSearch && !matchesQuickSearch(searchIndex, i, searchQuery)) continue;
    if (filters.every((f) => matchesFilter(rows[i], f, schema))) {
      result.push(i);
    }
  }
  return result;
};

export const filterLabel: FilterLabel = (f) => {
  switch (f.kind) {
    case 'contains': {
      const symbol = f.regex ? '~' : '~';
      const val = f.regex ? `/${f.text}/` : `"${f.text}"`;
      return `${f.column} ${symbol} ${val}`;
    }

    case 'in': {
      const shown = f.values.slice(0, 3).map(String);
      const rest = f.values.length > 3 ? ` +${f.values.length - 3}` : '';
      return `${f.column} ∈ {${shown.join(', ')}${rest}}`;
    }

    case 'range': {
      if (f.min !== undefined && f.max !== undefined) {
        return `${f.column} ${f.min}–${f.max}`;
      }
      if (f.min !== undefined) {
        return `${f.column} ≥ ${f.min}`;
      }
      if (f.max !== undefined) {
        return `${f.column} ≤ ${f.max}`;
      }
      return f.column;
    }

    case 'dateRange': {
      const from = f.from ?? '';
      const to = f.to ?? '';
      if (from && to) {
        return `${f.column} ${from} → ${to}`;
      }
      if (from) {
        return `${f.column} ≥ ${from}`;
      }
      if (to) {
        return `${f.column} ≤ ${to}`;
      }
      return f.column;
    }

    case 'missing': {
      return f.present ? `${f.column} is present` : `${f.column} is missing`;
    }

    case 'arrayElement': {
      const path = f.elementPath ? `${f.column}[].${f.elementPath}` : `${f.column}[]`;
      const op = f.op === 'eq' ? '=' : '~';
      return `${path} ${op} ${f.value}`;
    }

    case 'arrayLength': {
      if (f.min !== undefined && f.max !== undefined) {
        return `${f.column} length ${f.min}–${f.max}`;
      }
      if (f.min !== undefined) {
        return `${f.column} length ≥ ${f.min}`;
      }
      if (f.max !== undefined) {
        return `${f.column} length ≤ ${f.max}`;
      }
      return `${f.column} length`;
    }
  }
};
